/**
 * AddEthPanel — inline panel for bridging ETH to Robinhood Chain.
 *
 * Shown when a wallet has insufficient ETH for a trade or launch.
 * Two tabs: "Bridge ETH" (Arbitrum canonical bridge) and "Buy ETH" (on-ramp placeholder).
 *
 * Security:
 *   - Bridge addresses are fetched from the API server only.
 *   - The frontend never supplies or trusts bridge addresses.
 *   - No SYNAPZ treasury wallet is involved.
 *   - "Deposit time is estimated, not guaranteed" is prominently disclosed.
 */

import { useState } from "react";
import { formatEther } from "viem";
import { Card, Button, Field, inputClass } from "./ui";
import {
  BRIDGE_STATUS_LABELS,
  BRIDGE_STATUS_STEPS,
  type BridgeStatus,
} from "../config/bridgeConfig";
import { useBridgeFlow } from "../hooks/useBridgeFlow";
import { ACTIVE_CHAIN } from "../config/chains";

// ── Props ──────────────────────────────────────────────────────────────────────

interface AddEthPanelProps {
  /** Connected wallet address (null = not connected) */
  wallet: string | null;
  /** Current L2 ETH balance in wei (null = unknown) */
  currentL2Balance: bigint | null;
  /** Shortfall to prefill (null = no prefill) */
  shortfallWei: bigint | null;
  /** Where to return after funds arrive */
  returnContext: "trade" | "create" | null;
  onClose: () => void;
  onFundsArrived: (newBalance: bigint) => void;
}

// ── Progress bar ───────────────────────────────────────────────────────────────

function ProgressBar({ status }: { status: BridgeStatus }) {
  const stepIdx = BRIDGE_STATUS_STEPS.indexOf(status);
  const total = BRIDGE_STATUS_STEPS.length;

  if (status === "idle" || status === "loading_config" || status === "preparing") {
    return null;
  }

  const pct =
    status === "ready"
      ? 100
      : status === "failed" || status === "manual_redemption"
        ? null
        : stepIdx >= 0
          ? Math.round(((stepIdx + 1) / total) * 100)
          : 10;

  if (pct === null) return null;

  return (
    <div
      aria-label={`Bridge progress: ${BRIDGE_STATUS_LABELS[status]}`}
      className="space-y-1"
    >
      <div className="flex justify-end text-xs text-muted/60">
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-panel2 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${
            status === "ready" ? "bg-green-500" : "bg-plasma"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── Manual redemption instructions ────────────────────────────────────────────

function ManualRedemptionInstructions({ l1TxHash, destinationChainName }: {
  l1TxHash: `0x${string}` | null;
  destinationChainName: string;
}) {
  return (
    <div className="rounded-lg border border-warn/30 bg-warn/5 p-4 space-y-2 text-xs">
      <p className="font-semibold text-warn">Manual redemption required</p>
      <p className="text-muted/80">
        Your deposit was submitted on Ethereum but the automatic retryable ticket on{" "}
        <strong>{destinationChainName}</strong> was not redeemed automatically.
        This can happen if the L2 gas price spiked during the deposit window.
      </p>
      {l1TxHash && (
        <p className="text-muted/80">
          L1 transaction:{" "}
          <span className="font-mono break-all text-ink/70">{l1TxHash}</span>
        </p>
      )}
      <p className="font-medium text-warn/90">Recovery steps:</p>
      <ol className="list-decimal list-inside space-y-1 text-muted/70">
        <li>Open the official Arbitrum bridge interface for Robinhood Chain.</li>
        <li>Connect the same wallet that submitted the deposit.</li>
        <li>Navigate to "Pending transactions" or "Retryable tickets".</li>
        <li>Find the transaction with the hash above and select "Redeem".</li>
        <li>Approve the redemption transaction (standard L2 gas fee applies).</li>
      </ol>
      <p className="text-muted/60">
        Your ETH is not lost — it is held in the Arbitrum bridge. Redemption can
        be triggered at any time.
      </p>
    </div>
  );
}

// ── Bridge tab ─────────────────────────────────────────────────────────────────

function BridgeTab({
  wallet,
  currentL2Balance,
  shortfallWei,
  returnContext,
  onFundsArrived,
  onClose,
}: Omit<AddEthPanelProps, "onClose"> & { onClose: () => void }) {
  const { state, amountInput, setAmountInput, canSubmit, submit } = useBridgeFlow({
    wallet,
    destinationChainId: ACTIVE_CHAIN.chainId,
    currentL2Balance,
    shortfallWei,
    returnContext,
    onReady: () => {
      if (state.estimatedL2Balance !== null) {
        onFundsArrived(state.estimatedL2Balance);
      }
    },
  });

  const { config } = state;
  const isActive = !["idle", "preparing", "loading_config"].includes(state.status);
  const isTerminal = ["ready", "failed", "manual_redemption"].includes(state.status);

  // Derived gas cost display
  const gasCostEth =
    state.l1GasEstimate !== null && state.l1GasPrice !== null
      ? formatEther(state.l1GasEstimate * state.l1GasPrice)
      : null;

  return (
    <div className="space-y-4">
      {/* Config error */}
      {state.configError && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-xs text-danger/90">
          {state.configError}
        </div>
      )}

      {/* Progress */}
      <ProgressBar status={state.status} />

      {/* Ready state */}
      {state.status === "ready" && (
        <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-4 text-center space-y-2">
          <p className="text-sm font-semibold text-green-400">ETH arrived on Robinhood Chain</p>
          {state.estimatedL2Balance !== null && (
            <p className="text-xs text-muted/70">
              New balance: <span className="font-mono text-ink">{formatEther(state.estimatedL2Balance)} ETH</span>
            </p>
          )}
          <Button onClick={onClose} className="mt-2">
            Return to {returnContext === "create" ? "launch" : "trade"}
          </Button>
        </div>
      )}

      {/* Manual redemption */}
      {state.status === "manual_redemption" && config && (
        <ManualRedemptionInstructions
          l1TxHash={state.l1TxHash}
          destinationChainName={config.destinationChainName}
        />
      )}

      {/* Input form — shown while preparing */}
      {!isActive && !isTerminal && (
        <>
          <Field
            label="Amount to bridge (ETH)"
            hint={
              config
                ? `Min: ${formatEther(BigInt(config.minimumAmountWei))} ETH`
                : undefined
            }
          >
            <input
              type="number"
              min="0"
              step="0.001"
              className={inputClass}
              value={amountInput}
              onChange={e => setAmountInput(e.target.value)}
              placeholder="0.05"
              aria-label="ETH amount to bridge"
            />
          </Field>

          {/* Gas and time estimates */}
          {config && (
            <div className="rounded-lg border border-line/50 bg-panel2/50 p-3 space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-muted">Source</span>
                <span className="text-ink font-medium">{config.l1ChainName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Destination</span>
                <span className="text-ink font-medium">{config.destinationChainName}</span>
              </div>
              {gasCostEth && (
                <div className="flex justify-between">
                  <span className="text-muted">Estimated L1 gas</span>
                  <span className="font-mono text-ink/70">≈ {Number(gasCostEth).toFixed(6)} ETH</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted">Estimated time</span>
                <span className="text-ink/70">
                  ≈ {Math.round(config.estimatedDepositSeconds / 60)} min — not guaranteed
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Bridge</span>
                <span className="text-ink/70">Arbitrum canonical</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-muted">Inbox address</span>
                <span
                  className="font-mono text-ink/50 text-right break-all max-w-[180px]"
                  title="Server-verified bridge contract address"
                >
                  {config.inboxAddress}
                </span>
              </div>
            </div>
          )}

          {/* Gas warning */}
          {state.l1GasWarning && (
            <p className="text-xs text-warn/90 rounded border border-warn/30 bg-warn/5 px-3 py-2">
              You need ETH on {config?.l1ChainName ?? "Ethereum"} to pay the bridge transaction fee.
            </p>
          )}

          {/* Disclosure */}
          <p className="text-xs text-muted/50 leading-relaxed">
            This bridge uses the canonical Arbitrum deposit path. Deposit time is estimated and not
            guaranteed. Your wallet will be prompted to switch to{" "}
            {config?.l1ChainName ?? "Ethereum"} to sign the deposit. SYNAPZ never takes custody of
            your funds. Bridge addresses are verified by the SYNAPZ server.
          </p>

          {/* Submit */}
          <Button
            className="w-full"
            onClick={() => void submit()}
            disabled={!canSubmit || !wallet}
            data-testid="bridge-submit"
          >
            {!wallet
              ? "Connect wallet to bridge"
              : state.status === "loading_config"
                ? "Loading bridge config…"
                : state.configError
                  ? "Bridge disabled"
                  : "Continue in wallet"}
          </Button>
        </>
      )}

      {/* In-progress status (after submission, before terminal) */}
      {isActive && !isTerminal && (
        <div className="space-y-3">
          <div className="rounded-lg border border-plasma/20 bg-plasma/5 p-4 space-y-2 text-xs">
            <p className="text-muted/80 leading-relaxed">
              <strong className="text-ink">{BRIDGE_STATUS_LABELS[state.status]}.</strong>{" "}
              {state.status === "confirming_l1" &&
                "Waiting for Ethereum to confirm the deposit transaction."}
              {state.status === "depositing_l2" &&
                "The deposit is being processed on Robinhood Chain. This may take up to 15 minutes."}
              {state.status === "submitted_l1" &&
                "Your deposit was submitted. Waiting for Ethereum network confirmation."}
            </p>
            {state.l1TxHash && (
              <p className="font-mono text-muted/60 break-all">
                L1 tx: {state.l1TxHash}
              </p>
            )}
          </div>
          <p className="text-xs text-muted/50 text-center">
            This page will update automatically. You can safely refresh.
          </p>
        </div>
      )}

      {/* Error state */}
      {state.error && !["manual_redemption"].includes(state.status) && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-xs text-danger/90">
          {state.error}
        </div>
      )}
    </div>
  );
}

// ── On-ramp tab (placeholder) ─────────────────────────────────────────────────

function OnRampTab() {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-line/30 bg-panel2/50 p-5 text-center space-y-3">
        <p className="text-sm font-medium text-ink">Buy ETH via card or bank</p>
        <p className="text-xs text-muted/70 leading-relaxed">
          An in-app purchase option is coming soon. Provider selection, KYC requirements,
          and supported countries are being reviewed.
        </p>
        <p className="text-xs text-muted/50">
          In the meantime, purchase ETH on an exchange and bridge it using the Bridge ETH tab.
        </p>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

type PanelTab = "bridge" | "buy";

export function AddEthPanel({
  wallet,
  currentL2Balance,
  shortfallWei,
  returnContext,
  onClose,
  onFundsArrived,
}: AddEthPanelProps) {
  const [tab, setTab] = useState<PanelTab>("bridge");

  return (
    <Card accent className="space-y-4" data-testid="add-eth-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">Add ETH to Robinhood Chain</h3>
        <button
          onClick={onClose}
          className="text-muted/60 hover:text-ink transition-colors text-xs px-2 py-1"
          aria-label="Close add ETH panel"
          data-testid="add-eth-close"
        >
          ✕
        </button>
      </div>

      {/* Shortfall info */}
      {shortfallWei !== null && shortfallWei > 0n && (
        <div className="rounded-lg border border-warn/30 bg-warn/5 px-3 py-2 text-xs text-warn/90">
          You need approximately{" "}
          <span className="font-mono font-semibold">{Number(formatEther(shortfallWei)).toFixed(4)} ETH</span>{" "}
          more on Robinhood Chain to proceed.
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex rounded-lg border border-line overflow-hidden">
        {([
          ["bridge", "Bridge ETH"],
          ["buy", "Buy ETH"],
        ] as [PanelTab, string][]).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 py-2 text-sm font-semibold transition-colors ${
              tab === t
                ? "bg-plasma/15 text-plasma"
                : "text-muted hover:text-ink"
            }`}
            data-testid={`add-eth-tab-${t}`}
          >
            {label}{t === "buy" ? " (soon)" : ""}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "bridge" ? (
        <BridgeTab
          wallet={wallet}
          currentL2Balance={currentL2Balance}
          shortfallWei={shortfallWei}
          returnContext={returnContext}
          onClose={onClose}
          onFundsArrived={onFundsArrived}
        />
      ) : (
        <OnRampTab />
      )}
    </Card>
  );
}
